import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LICENSE_STATUS_LABELS,
  type AnnouncementDTO,
  MODULE_LABELS,
  PLAN_LABELS,
  planAddons,
  isBifiOnly,
  type LicenseStatus,
  type NotificationSummary,
  type PosSettings,
} from '@bifeai/shared';
import { TouchKeypad } from './TouchKeypad';
import { SetupWidget } from './SetupWidget';
import { HelpButton, TourHost } from './Tour';
import { AppShell, Badge, Brand, BrandMark, Popover, Sidebar, ThemeToggle, Topbar, useShell, type NavGroupDef } from '@bifeai/ui';
import {
  Beef,
  Boxes,
  ClipboardList,
  MessagesSquare,
  Settings2,
  ChartColumn,
  Clock,
  CookingPot,
  HandCoins,
  Info,
  LayoutDashboard,
  Network,
  PackageMinus,
  Receipt,
  ReceiptText,
  Scale,
  ShieldCheck,
  ShoppingCart,
  Tags,
  Truck,
  ArrowLeftRight,
  BadgeCheck,
  UserCog,
  UserRound,
  Wallet,
  Building2,
  Container,
  Smartphone,
  Percent,
} from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router';
import { api } from '../lib/api';
import { setOfflineUser, startOfflineSync, useOffline } from '../lib/offline';
import { formatDate, formatDateTime } from '../lib/format';
import { prefetchRoute } from '../lib/prefetch';
import { useSession } from '../lib/session';
import { BranchPicker } from './BranchPicker';
import { NotificationsBell } from './NotificationsBell';
import { OrdersBell } from './OrdersBell';
import { useOrdersSummary } from '../pages/orders-chat/shared';
import { UserMenu } from './UserMenu';

const TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/alertas': 'Alertas',
  '/sucursales': 'Sucursales',
  '/franquicias': 'Franquicias',
  '/mi-franquicia': 'Mi franquicia',
  '/usuarios': 'Usuarios',
  '/cuenta': 'Mi cuenta',
  '/plan': 'Mi plan',
  '/productos': 'Productos',
  '/precios': 'Precios',
  '/proveedores': 'Proveedores',
  '/compras': 'Compras',
  '/compras/nueva': 'Nueva compra',
  '/stock': 'Stock',
  '/transferencias': 'Transferencias',
  '/venta': 'Punto de venta',
  '/caja': 'Caja',
  '/ventas': 'Ventas',
  '/configuracion': 'Configuración',
  '/clientes': 'Clientes y fiado',
  '/mermas': 'Mermas y tacho',
  '/produccion': 'Producción',
  '/desposte': 'Desposte',
  '/control': 'Control de faltantes',
  '/empleados': 'Empleados',
  '/asistencia': 'Asistencia',
  '/estadisticas': 'Estadísticas',
  '/despostero': 'Despostero',
  '/chat': 'Chat',
  '/pedidos': 'Pedidos',
  '/pedidos/configuracion': 'Configurar agente',
  '/pedidos/estadisticas': 'Estadísticas de bifi',
  '/mayorista': 'Pedidos mayoristas',
  '/mayorista/clientes': 'Clientes mayoristas',
  '/mayorista/listas': 'Listas de precios',
  '/mayorista/rutas': 'Hojas de ruta',
  '/mayorista/comisiones': 'Comisiones',
  '/mayorista/cobros': 'Cobros y rendición',
  '/mayorista/estadisticas': 'Estadísticas mayorista',
  '/mayorista/configuracion': 'Configurar mayorista',
};

function titleFor(pathname: string): string {
  if (TITLES[pathname]) return TITLES[pathname];
  if (pathname.startsWith('/compras/')) return 'Compra';
  if (pathname.startsWith('/clientes/')) return 'Cuenta corriente';
  if (pathname === '/proveedores/pagos') return 'Pagos a proveedores';
  if (pathname.startsWith('/proveedores/')) return 'Cuenta del proveedor';
  if (pathname.startsWith('/empleados/')) return 'Empleado';
  return 'BifeAI';
}

function SidebarBrand() {
  const { collapsed } = useShell();
  return (
    <Link to="/" aria-label="BifeAI, ir al dashboard">
      {collapsed ? <BrandMark /> : <Brand />}
    </Link>
  );
}

export function AppLayout() {
  const { me, can, multiBranch, franchisesEnabled, isFranchiseUser, isHeadquartersUser } = useSession();
  const { pathname } = useLocation();

  // Cuenta dada de baja: el dueño solo puede entrar a Mi plan (renovar o descargar sus datos).
  const billingOnly = !!me.tenant.billingOnly;
  // Solo bifi: la cuenta tiene el agente de ventas y lo que necesita (productos, precios, clientes y pedidos).
  const bifiOnly = isBifiOnly(me.tenant.plan);
  const summary = useQuery({
    queryKey: ['notifications', 'summary'],
    queryFn: () => api.get<NotificationSummary>('/notifications/summary'),
    refetchInterval: 60_000,
    enabled: !billingOnly,
  });
  // Comunicación de BifeAI (campanita) y avisos de servicio (barra arriba mientras dure).
  const news = useQuery({
    queryKey: ['announcements'],
    queryFn: () => api.get<AnnouncementDTO[]>('/announcements'),
    enabled: !billingOnly,
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
  const service = (news.data ?? []).find((a) => a.kind === 'SERVICE' && a.serviceEnd && new Date(a.serviceEnd) > new Date());
  const posSettings = useQuery({
    queryKey: ['pos', 'settings'],
    queryFn: () => api.get<PosSettings>('/pos/settings'),
    enabled: !billingOnly && (can('pos.sell') || can('settings.manage')),
    staleTime: 60_000,
  });
  const externalButcher = posSettings.data?.team.externalButcher ?? false;
  // Pedidos: el tablero es de todos los planes (con su campanita); el chat y bifi, del adicional.
  const ordersChat = !billingOnly && me.tenant.modules.includes('ORDERS_CHAT');
  const canOrders = !billingOnly && can('orders.manage');
  const canChat = ordersChat && canOrders;
  const canAgentSettings = ordersChat && (can('settings.manage') || can('franchise.accounts'));
  const orders = useOrdersSummary(canOrders);
  const sections = summary.data?.bySection ?? {};
  const unread = summary.data?.unread ?? 0;
  const at = (path: string) => (path === '/' ? pathname === '/' : pathname.startsWith(path));
  // Mayorista: canal propio de la marca (no lo ve una franquicia).
  const wholesale =
    !billingOnly &&
    !isFranchiseUser &&
    me.tenant.modules.includes('WHOLESALE') &&
    (can('wholesale.manage') || can('wholesale.sell') || can('wholesale.deliver'));
  const wsManage = can('wholesale.manage');
  // El vendedor ve sus pedidos y clientes; el repartidor, las hojas de ruta y su reparto (si hace las dos cosas, todo).
  const wsSell = wsManage || can('wholesale.sell');
  const wsDeliver = wsManage || can('wholesale.deliver');

  // Las secciones marcadas "Pronto" se activan en los próximos bloques de desarrollo.
  const groups: NavGroupDef[] = [
    {
      key: 'main',
      items: [
        {
          key: 'dash',
          to: '/',
          label: 'Dashboard',
          icon: <LayoutDashboard />,
          active: at('/'),
        },
        // Las alertas están en la campanita de arriba (y su pantalla, en "Ver todas").
        // Franquicias: su propia sección, separada de la operación de los locales propios.
        ...(franchisesEnabled && isHeadquartersUser && can('branches.manage')
          ? [{ key: 'franchises', to: '/franquicias', label: 'Franquicias', icon: <Network />, active: at('/franquicias') }]
          : []),
        ...(franchisesEnabled && isFranchiseUser
          ? [{ key: 'my-franchise', to: '/mi-franquicia', label: 'Mi franquicia', icon: <Network />, active: at('/mi-franquicia') }]
          : []),
      ],
    },
    {
      key: 'counter',
      title: 'Mostrador',
      items: [
        ...(can('pos.sell')
          ? [
              {
                key: 'pos',
                to: '/venta',
                label: 'Punto de venta',
                icon: <ShoppingCart />,
                active: at('/venta'),
              },
            ]
          : []),
        ...(can('pos.sell') || can('cash.own')
          ? [
              {
                key: 'cash',
                to: '/caja',
                label: 'Caja',
                icon: <Wallet />,
                active: at('/caja'),
              },
            ]
          : []),
        ...(can('pos.sell') || can('sales.void') || can('dashboard.view')
          ? [
              {
                key: 'sales',
                to: '/ventas',
                label: 'Ventas',
                icon: <Receipt />,
                active: pathname === '/ventas',
              },
            ]
          : []),
        ...(can('pos.sell') || can('customers.manage')
          ? [
              {
                key: 'customers',
                to: '/clientes',
                label: 'Clientes y fiado',
                icon: <HandCoins />,
                count: sections.clientes,
                active: at('/clientes'),
              },
            ]
          : []),
      ],
    },
    ...(canOrders || canAgentSettings
      ? [
          {
            key: 'orders-chat',
            title: ordersChat ? 'Pedidos y chat' : 'Pedidos',
            items: [
              ...(canChat
                ? [
                    {
                      key: 'chat',
                      to: '/chat',
                      label: 'Chat',
                      icon: <MessagesSquare />,
                      count: orders.data?.unreadConversations || undefined,
                      active: at('/chat'),
                    },
                  ]
                : []),
              ...(canOrders
                ? [
                    {
                      key: 'orders',
                      to: '/pedidos',
                      label: 'Pedidos',
                      icon: <ClipboardList />,
                      count: orders.data?.newOrders || undefined,
                      active: pathname === '/pedidos',
                    },
                  ]
                : []),
              ...(ordersChat && (canOrders || canAgentSettings)
                ? [
                    {
                      key: 'bifi-stats',
                      to: '/pedidos/estadisticas',
                      label: 'Estadísticas de bifi',
                      icon: <ChartColumn />,
                      active: at('/pedidos/estadisticas'),
                    },
                  ]
                : []),
              ...(canAgentSettings
                ? [
                    {
                      key: 'agent',
                      to: '/pedidos/configuracion',
                      label: 'Configurar agente',
                      icon: <Settings2 />,
                      active: at('/pedidos/configuracion'),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(wholesale
      ? [
          {
            key: 'wholesale',
            title: 'Mayorista y reparto',
            items: [
              ...(wsSell
                ? [
                    { key: 'ws-orders', to: '/mayorista', label: 'Pedidos mayoristas', icon: <Container />, active: pathname === '/mayorista' },
                    {
                      key: 'ws-customers',
                      to: '/mayorista/clientes',
                      label: 'Clientes mayoristas',
                      icon: <Building2 />,
                      active: at('/mayorista/clientes'),
                    },
                  ]
                : []),
              ...(wsManage
                ? [{ key: 'ws-lists', to: '/mayorista/listas', label: 'Listas de precios', icon: <Tags />, active: at('/mayorista/listas') }]
                : []),
              ...(wsDeliver
                ? [
                    { key: 'ws-routes', to: '/mayorista/rutas', label: 'Hojas de ruta', icon: <Truck />, active: at('/mayorista/rutas') },
                    { key: 'ws-driver', to: '/reparto', label: 'Mi reparto (celular)', icon: <Smartphone />, active: false },
                  ]
                : []),
              { key: 'ws-commissions', to: '/mayorista/comisiones', label: 'Comisiones', icon: <Percent />, active: at('/mayorista/comisiones') },
              { key: 'ws-money', to: '/mayorista/cobros', label: 'Cobros y rendición', icon: <Wallet />, active: at('/mayorista/cobros') },
              ...(wsManage
                ? [
                    {
                      key: 'ws-stats',
                      to: '/mayorista/estadisticas',
                      label: 'Estadísticas mayorista',
                      icon: <ChartColumn />,
                      active: at('/mayorista/estadisticas'),
                    },
                    {
                      key: 'ws-settings',
                      to: '/mayorista/configuracion',
                      label: 'Configurar mayorista',
                      icon: <Settings2 />,
                      active: at('/mayorista/configuracion'),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      key: 'goods',
      title: 'Mercadería',
      items: [
        {
          key: 'products',
          to: '/productos',
          label: 'Productos',
          icon: <Beef />,
          active: at('/productos'),
        },
        ...(can('purchases.manage')
          ? [
              {
                key: 'purchases',
                to: '/compras',
                label: 'Compras',
                icon: <ReceiptText />,
                count: sections.compras,
                active: at('/compras'),
              },
            ]
          : []),
        ...(can('purchases.manage') || can('stock.waste')
          ? [
              {
                key: 'stock',
                to: '/stock',
                label: 'Stock',
                icon: <Boxes />,
                count: sections.stock,
                active: at('/stock'),
              },
            ]
          : []),
        ...(me.tenant.modules.includes('MULTI_BRANCH') && !isFranchiseUser && can('transfers.manage')
          ? [{ key: 'transfers', to: '/transferencias', label: 'Transferencias', icon: <ArrowLeftRight />, active: at('/transferencias') }]
          : []),
        ...(can('stock.waste')
          ? [
              { key: 'production', to: '/produccion', label: 'Producción', icon: <CookingPot />, active: at('/produccion') },
              { key: 'waste', to: '/mermas', label: 'Mermas y tacho', icon: <PackageMinus />, active: at('/mermas') },
            ]
          : []),
        ...(can('purchases.manage') || can('audit.view')
          ? [
              {
                key: 'control',
                to: '/control',
                label: 'Control de faltantes',
                icon: <ShieldCheck />,
                count: sections.control,
                active: at('/control'),
              },
            ]
          : []),
        ...(can('cutting.manage')
          ? [
              {
                key: 'cutting',
                to: '/desposte',
                label: me.tenant.modules.includes('CUTTING') ? 'Desposte automático' : 'Desposte manual',
                icon: <Scale />,
                active: at('/desposte'),
              },
            ]
          : []),
      ],
    },
    {
      key: 'admin',
      title: 'Administración',
      items: [
        ...(can('suppliers.manage') || can('purchases.manage')
          ? [
              {
                key: 'suppliers',
                to: '/proveedores',
                label: 'Proveedores',
                icon: <Truck />,
                count: sections.proveedores,
                active: at('/proveedores'),
              },
            ]
          : []),
        ...(externalButcher && (can('cutting.manage') || can('suppliers.manage'))
          ? [{ key: 'butcher', to: '/despostero', label: 'Despostero', icon: <UserCog />, active: at('/despostero') }]
          : []),
        ...(can('employees.manage') || can('employees.advances')
          ? [
              { key: 'employees', to: '/empleados', label: 'Empleados', icon: <UserRound />, active: at('/empleados') },
              { key: 'attendance', to: '/asistencia', label: 'Asistencia', icon: <Clock />, active: at('/asistencia') },
            ]
          : []),
        ...(can('prices.manage') || can('costs.view')
          ? [
              {
                key: 'prices',
                to: '/precios',
                label: 'Precios',
                icon: <Tags />,
                active: at('/precios'),
              },
            ]
          : []),
        ...(can('stats.global')
          ? [{ key: 'stats', to: '/estadisticas', label: 'Estadísticas', icon: <ChartColumn />, active: at('/estadisticas') }]
          : []),
      ],
    },
  ];

  const itemsOf = (key: string, ...keys: string[]) => keys.flatMap((k) => groups.find((g) => g.key === key)?.items.filter((i) => i.key === k) ?? []);
  const group = (key: string, title: string, items: NavGroupDef['items']): NavGroupDef | null => (items.length ? { key, title, items } : null);
  // Solo bifi: lo de todos los días arriba (Pedidos, Chat y el agente), después el catálogo, los clientes y las franquicias.
  const bifiGroups = [
    group('bifi', 'bifi', itemsOf('orders-chat', 'orders', 'chat', 'bifi-stats', 'agent')),
    group('catalog', 'Catálogo', [...itemsOf('goods', 'products'), ...itemsOf('admin', 'prices')]),
    // Sin mostrador: los clientes van sueltos, sin fiado.
    group(
      'customers',
      'Clientes',
      itemsOf('counter', 'customers').map((i) => ({ ...i, label: 'Clientes' })),
    ),
    group('franchises', 'Franquicias', itemsOf('main', 'franchises', 'my-franchise')),
  ].filter((g): g is NavGroupDef => g !== null);

  const navGroups: NavGroupDef[] = billingOnly
    ? [{ key: 'main', items: [{ key: 'plan', to: '/plan', label: 'Mi plan', icon: <BadgeCheck />, active: true }] }]
    : bifiOnly
      ? bifiGroups
      : groups;

  // Al entrar, se cargan de a poco los datos de las secciones del menú: después se navega sin esperas.
  const qc = useQueryClient();
  const navTargets = navGroups.flatMap((g) => g.items.map((i) => i.to)).join('|');
  useEffect(() => {
    const targets = navTargets.split('|');
    const timers = targets.map((to, i) => setTimeout(() => prefetchRoute(qc, to), 1500 + i * 250));
    return () => timers.forEach(clearTimeout);
  }, [navTargets, qc]);

  // Ventas cobradas sin internet: se suben solas cuando vuelve la conexión.
  useEffect(() => {
    setOfflineUser(me.user.id);
    return startOfflineSync();
  }, [me.user.id]);
  const offline = useOffline(me.user.id);

  const license = me.tenant.licenseStatus;
  const licenseTone = license === 'ACTIVE' ? 'success' : license === 'GRACE' ? 'warning' : 'danger';

  return (
    <AppShell
      sidebar={
        <Sidebar brand={<SidebarBrand />} groups={navGroups} Link={NavLink} footer={isFranchiseUser || billingOnly ? undefined : <PlanLine />} />
      }
    >
      <Topbar title={titleFor(pathname)}>
        {license !== 'ACTIVE' && (
          <span className="bf-hide-mobile">
            <Badge tone={licenseTone} dot>
              Licencia: {LICENSE_STATUS_LABELS[license].toLowerCase()}
            </Badge>
          </span>
        )}
        {multiBranch && <BranchPicker />}
        <span className="bf-hide-mobile">
          <ThemeToggle />
        </span>
        {!billingOnly && <HelpButton />}
        {canOrders && <OrdersBell summary={orders.data} />}
        {!billingOnly && <NotificationsBell unread={unread} announcements={news.data} />}
        <UserMenu />
      </Topbar>
      <main className="bf-content" id="contenido">
        {billingOnly ? (
          pathname === '/plan' ? (
            <Outlet />
          ) : (
            <Navigate to="/plan" replace />
          )
        ) : (
          <>
            {bifiOnly && (pathname === '/' || BIFI_HIDDEN.some((p) => pathname.startsWith(p))) && <Navigate to="/pedidos" replace />}
            {service && (
              <div className="bf-offline-bar" role="status">
                <strong>{service.title}:</strong> {formatDateTime(service.serviceStart!)} a {formatDateTime(service.serviceEnd!)}. {service.body}
              </div>
            )}
            <LicenseBar status={license} canPay={can('settings.manage') || me.membership.role === 'FRANCHISEE'} onPlan={pathname === '/plan'} />
            {me.tenant.termsPending && pathname !== '/plan' && (
              <div className="bf-offline-bar" role="status">
                <strong>Te falta firmar los términos y condiciones de BifeAI.</strong> <Link to="/plan#terminos">Leerlos y firmar</Link>
              </div>
            )}
            <OfflineBar online={offline.online} pending={offline.pending} failed={offline.failed.length} syncing={offline.syncing} />
            <Outlet />
            <TouchKeypad />
            <SetupWidget />
            <TourHost />
          </>
        )}
      </main>
    </AppShell>
  );
}

/** Secciones de la plataforma que Solo bifi no tiene (si se entra por link, va a Pedidos). */
const BIFI_HIDDEN = [
  '/venta',
  '/caja',
  '/ventas',
  '/compras',
  '/stock',
  '/transferencias',
  '/produccion',
  '/mermas',
  '/control',
  '/desposte',
  '/proveedores',
  '/despostero',
  '/empleados',
  '/asistencia',
  '/estadisticas',
];

/** Ítem del menú: al pasar el mouse o tocarlo ya pide los datos de esa sección. */
function NavLink(props: { to: string; className?: string; children?: ReactNode; 'aria-current'?: 'page'; onClick?: () => void }) {
  const qc = useQueryClient();
  const warm = () => prefetchRoute(qc, props.to);
  return <Link {...props} onMouseEnter={warm} onFocus={warm} onTouchStart={warm} />;
}

/** Plan y adicionales en una sola línea; el detalle se ve al tocarla. */
function PlanLine() {
  const { me, can } = useSession();
  const addons = planAddons(me.tenant.plan, me.tenant.modules);
  return (
    <Popover
      trigger={
        <button type="button" className="bf-plan-line">
          <strong>Plan {PLAN_LABELS[me.tenant.plan]}</strong>
          <span>{addons.length ? `+${addons.length}` : ''}</span>
          <Info size={14} aria-hidden />
        </button>
      }
    >
      <div className="bf-plan-pop">
        <strong>Plan {PLAN_LABELS[me.tenant.plan]}</strong>
        {addons.map((m) => (
          <span key={m}>+ {MODULE_LABELS[m]}</span>
        ))}
        <span className="bf-hint">{me.tenant.serviceUntil ? `Servicio hasta el ${formatDate(me.tenant.serviceUntil)}` : 'Servicio incluido'}</span>
        {can('settings.manage') && <Link to="/plan">Ver mi plan y sumar módulos</Link>}
      </div>
    </Popover>
  );
}

/** Licencia en gracia o solo lectura: aviso arriba de todo, con el camino para pagar. */
function LicenseBar({ status, canPay, onPlan }: { status: LicenseStatus; canPay: boolean; onPlan: boolean }) {
  if ((status !== 'GRACE' && status !== 'READ_ONLY') || onPlan) return null;
  return (
    <div className={`bf-offline-bar${status === 'READ_ONLY' ? ' is-off' : ''}`} role="status">
      {status === 'GRACE' ? (
        <strong>No se pudo cobrar la licencia.</strong>
      ) : (
        <strong>Licencia en solo lectura: podés ver y descargar, pero no vender ni cargar datos.</strong>
      )}{' '}
      {canPay ? <Link to="/plan">Pagar ahora</Link> : 'Avisale al dueño de la carnicería.'}
    </div>
  );
}

/** Aviso de conexión: sin internet se sigue vendiendo y las ventas se suben solas al volver. */
function OfflineBar({ online, pending, failed, syncing }: { online: boolean; pending: number; failed: number; syncing: boolean }) {
  if (online && !pending && !failed) return null;
  const ventas = (n: number) => (n === 1 ? '1 venta' : `${n} ventas`);
  return (
    <div className={`bf-offline-bar${online ? '' : ' is-off'}`} role="status" aria-live="polite">
      {!online ? (
        <>
          <strong>Sin internet.</strong> Podés seguir vendiendo en efectivo o con tarjeta en el posnet: las ventas se guardan en esta computadora
          {pending ? ` (${ventas(pending)} para subir)` : ''} y se suben solas cuando vuelva la conexión.
        </>
      ) : pending ? (
        <>{syncing ? `Subiendo ${ventas(pending)} hechas sin internet…` : `${ventas(pending)} hechas sin internet esperan subirse.`}</>
      ) : null}
      {failed > 0 && (
        <>
          {' '}
          <Link to="/caja">{ventas(failed)} sin internet no se pudieron subir: revisalas en Caja.</Link>
        </>
      )}
    </div>
  );
}
